import React, { useState, useEffect, useCallback } from 'react';
import { Task, TaskStatus, ShootingMode, ProductCategory, STATUS_CONFIG, CATEGORY_CONFIG, SHOOTING_MODE_CONFIG } from '../types';
import { getTasks, addTask, updateTask, deleteTask, getProgressStats } from '../utils/taskStorage';
import './TaskManager.css';

const TaskManager: React.FC = () => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [stats, setStats] = useState({ total:0, pending:0, filming:0, editing:0, completed:0, percentage:0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  const [showModal, setShowModal] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // 表单字段
  const [formData, setFormData] = useState({
    skuCode: '',
    productName: '',
    category: 'padlock' as ProductCategory,
    isCore: false,
    shootingMode: 'single' as ShootingMode,
    shootingNotes: '',
    status: 'pending' as TaskStatus
  });

  // 从服务器加载任务数据
  const loadTasks = useCallback(async () => {
    try {
      setLoading(true);
      const taskList = await getTasks();
      setTasks(taskList || []);
      
      // 计算统计
      const statData = await getProgressStats();
      setStats(statData);
    } catch (error) {
      console.error('加载数据失败:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  const filteredTasks = tasks.filter(task => {
    const matchSearch =
      task.skuCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
      task.productName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchCat = filterCategory === 'all' || task.category === filterCategory;
    const matchSts = filterStatus === 'all' || task.status === filterStatus;
    return matchSearch && matchCat && matchSts;
  });

  const handleAddNew = useCallback(() => {
    setEditingTask(null);
    setFormData({ skuCode:'', productName:'', category:'padlock', isCore:false, shootingMode:'single', shootingNotes:'', status:'pending' });
    setShowModal(true);
  }, []);

  const handleEdit = (task: Task) => {
    setEditingTask(task);
    setFormData({
      skuCode: task.skuCode,
      productName: task.productName,
      category: task.category,
      isCore: task.isCore,
      shootingMode: task.shootingMode,
      shootingNotes: task.shootingNotes || '',
      status: task.status
    });
    setShowModal(true);
  };

  // 状态循环切换
  const statusCycle: Record<TaskStatus, TaskStatus> = {
    pending: 'filming',
    filming: 'editing',
    editing: 'completed',
    completed: 'pending'
  };

  const handleToggleStatus = async (taskId: string) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;
    
    try {
      await updateTask(taskId, { status: statusCycle[task.status] });
      await loadTasks(); // 刷新列表和统计
    } catch (error) {
      console.error('切换状态失败:', error);
    }
  };

  const handleSubmit = async () => {
    if (!formData.skuCode.trim() || !formData.productName.trim()) return;

    try {
      if (editingTask) {
        await updateTask(editingTask.id, formData);
      } else {
        await addTask(formData as any);
      }
      await loadTasks(); // 刷新
      setShowModal(false);
      setEditingTask(null);
    } catch (error) {
      console.error('保存任务失败:', error);
    }
  };

  const handleDelete = async (taskId: string) => {
    try {
      await deleteTask(taskId);
      await loadTasks();
      setDeleteConfirmId(null);
    } catch (error) {
      console.error('删除任务失败:', error);
    }
  };

  // 状态按钮样式映射
  const statusClassMap: Record<TaskStatus, string> = {
    pending: 'status-pending',
    filming: 'status-filming',
    editing: 'status-editing',
    completed: 'status-completed'
  };

  if (loading) {
    return (
      <div className="app-main" style={{ display:'flex', justifyContent:'center', alignItems:'center', minHeight:'400px' }}>
        <div style={{ textAlign:'center' }}>
          <div style={{ fontSize:'32px', marginBottom:'12px' }}>⏳</div>
          <p style={{ color:'var(--text-muted)' }}>正在加载数据...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="app-main">
      {/* 页面标题 */}
      <div style={{ marginBottom:'22px', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:'10px' }}>
        <div>
          <h1 style={{ fontSize:'24px', fontWeight:800, color:'var(--text)', marginBottom:'3px' }}>✅ 任务管理</h1>
          <p style={{ fontSize:'13.5px', color:'var(--text-muted)' }}>共 {tasks.length} 个拍摄任务 · 管理 SKU 拍摄进度</p>
        </div>
        <button className="btn btn-primary" onClick={handleAddNew}>➕ 新增任务</button>
      </div>

      {/* 进度概览 */}
      <section className="progress-overview">
        <div className="prog-card highlight">
          <div className="prog-label">总完成率</div>
          <div className="prog-value">{stats.percentage}%</div>
          <div className="prog-sub">{stats.completed} / {stats.total} 已完成</div>
        </div>
        <div className="prog-card">
          <div className="prog-label">未开始</div>
          <div className="prog-value" style={{ fontSize:'24px', color:'#9ca3af' }}>{stats.pending}</div>
          <div className="prog-sub">等待排期</div>
        </div>
        <div className="prog-card">
          <div className="prog-label">🎬 拍摄中</div>
          <div className="prog-value" style={{ fontSize:'24px', color:'var(--warning)' }}>{stats.filming}</div>
          <div className="prog-sub">正在执行</div>
        </div>
        <div className="prog-card">
          <div className="prog-label">✂️ 剪辑中</div>
          <div className="prog-value" style={{ fontSize:'24px', color:'var(--info)' }}>{stats.editing}</div>
          <div className="prog-sub">后期制作</div>
        </div>
        <div className="prog-card">
          <div className="prog-label">✅ 已完成</div>
          <div className="prog-value" style={{ fontSize:'24px', color:'var(--success)' }}>{stats.completed}</div>
          <div className="prog-sub">可以上架</div>
        </div>
      </section>

      {/* 进度条 */}
      {stats.total > 0 && (
        <div style={{ marginBottom:'22px' }}>
          <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'6px' }}>
            <span style={{ fontSize:'12px', color:'var(--text-muted)', fontWeight:600 }}>总体进度</span>
            <span style={{ fontSize:'12px', color:'var(--primary)', fontWeight:700 }}>{stats.percentage}%</span>
          </div>
          <div className="progress-bar-wrap">
            <div className="progress-bar-fill" style={{ width:`${stats.percentage}%` }} />
          </div>
        </div>
      )}

      {/* 工具栏 */}
      <section className="task-toolbar">
        <div className="toolbar-left">
          <input
            type="text"
            className="search-input"
            placeholder="🔍 搜索 SKU 编号或产品名称..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
          <select className="filter-select" value={filterCategory} onChange={e => setFilterCategory(e.target.value)}>
            <option value="all">全部分类</option>
            {Object.entries(CATEGORY_CONFIG).map(([k,v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
          <select className="filter-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="all">全部状态</option>
            {Object.entries(STATUS_CONFIG).map(([k,v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
        </div>
        <div className="toolbar-right">
          <span style={{ fontSize:'12px', color:'var(--text-muted)' }}>显示 {filteredTasks.length} / {tasks.length} 条</span>
        </div>
      </section>

      {/* 任务表格 */}
      <section className="task-table-container">
        {filteredTasks.length > 0 ? (
          <table className="task-table">
            <thead>
              <tr>
                <th>SKU 编号</th>
                <th>产品名称</th>
                <th>分类</th>
                <th>核心</th>
                <th>拍摄方式</th>
                <th>状态</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {filteredTasks.map(task => (
                <tr key={task.id}>
                  <td><span className="sku-code">{task.skuCode}</span></td>
                  <td>
                    <div className="product-name-cell" title={task.productName}>{task.productName}</div>
                    {task.shootingNotes && (
                      <div style={{ fontSize:'11px', color:'var(--text-muted)', marginTop:'2px', maxWidth:'180px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        📝 {task.shootingNotes}
                      </div>
                    )}
                  </td>
                  <td><span className="tag tag-gray">{CATEGORY_CONFIG[task.category]?.label || task.category}</span></td>
                  <td>{task.isCore ? <span className="tag tag-red">★ 核心产品</span> : <span style={{ color:'var(--text-muted)', fontSize:'12px' }}>—</span>}</td>
                  <td><span className="tag tag-gray">{SHOOTING_MODE_CONFIG[task.shootingMode]?.label}</span></td>
                  <td>
                    <button
                      className={`status-btn ${statusClassMap[task.status]}`}
                      onClick={() => handleToggleStatus(task.id)}
                      title="点击切换状态"
                    >
                      {STATUS_CONFIG[task.status].label}
                    </button>
                  </td>
                  <td>
                    <div className="action-btns">
                      <button className="btn btn-success btn-sm" onClick={() => handleEdit(task)}>✏️ 编辑</button>
                      {deleteConfirmId === task.id ? (
                        <>
                          <button className="btn btn-danger btn-sm" onClick={() => handleDelete(task.id)}>确认删除</button>
                          <button className="btn btn-outline btn-sm" onClick={() => setDeleteConfirmId(null)}>取消</button>
                        </>
                      ) : (
                        <button className="btn btn-danger btn-sm" onClick={() => setDeleteConfirmId(task.id)}>🗑️ 删除</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="empty-state">
            <div className="empty-state-icon">📋</div>
            <p>暂无匹配的任务</p>
            <p style={{ fontSize:'12px', marginTop:'4px' }}>点击上方"新增任务"添加第一个拍摄任务</p>
          </div>
        )}
      </section>

      {/* 新增/编辑模态框 */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingTask ? '✏️ 编辑任务' : '➕ 新增拍摄任务'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>SKU 编号 *</label>
                <input type="text" className="form-control" placeholder="例: PL-001、BD-002"
                  value={formData.skuCode} onChange={e => setFormData(p => ({ ...p, skuCode:e.target.value }))} />
              </div>
              <div className="form-group">
                <label>产品名称 *</label>
                <input type="text" className="form-control" placeholder="例: 钢制安全挂锁 38mm"
                  value={formData.productName} onChange={e => setFormData(p => ({ ...p, productName:e.target.value }))} />
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px' }}>
                <div className="form-group">
                  <label>产品分类</label>
                  <select className="form-select" value={formData.category}
                    onChange={e => setFormData(p => ({ ...p, category:e.target.value as ProductCategory }))}>
                    {Object.entries(CATEGORY_CONFIG).map(([k,cfg]) => (
                      <option key={k} value={k}>{cfg.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>拍摄方式</label>
                  <select className="form-select" value={formData.shootingMode}
                    onChange={e => setFormData(p => ({ ...p, shootingMode:e.target.value as ShootingMode }))}>
                    {Object.entries(SHOOTING_MODE_CONFIG).map(([k,cfg]) => (
                      <option key={k} value={k}>{cfg.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px' }}>
                <div className="form-group">
                  <label>初始状态</label>
                  <select className="form-select" value={formData.status}
                    onChange={e => setFormData(p => ({ ...p, status:e.target.value as TaskStatus }))}>
                    {Object.entries(STATUS_CONFIG).map(([k,cfg]) => (
                      <option key={k} value={k}>{cfg.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group" style={{ display:'flex', flexDirection:'column', justifyContent:'flex-end' }}>
                  <label className="checkbox-label">
                    <input type="checkbox" checked={formData.isCore}
                      onChange={e => setFormData(p => ({ ...p, isCore:e.target.checked }))} />
                    <span>⭐ 核心产品（优先拍摄）</span>
                  </label>
                </div>
              </div>
              <div className="form-group">
                <label>特殊说明 / 拍摄要求</label>
                <textarea className="form-textarea" rows={3} placeholder="拍摄要求、重点说明、特殊注意事项..."
                  value={formData.shootingNotes}
                  onChange={e => setFormData(p => ({ ...p, shootingNotes:e.target.value }))} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowModal(false)}>取消</button>
              <button
                className="btn btn-primary"
                onClick={handleSubmit}
                disabled={!formData.skuCode.trim() || !formData.productName.trim()}
                style={{ opacity:(!formData.skuCode.trim()||!formData.productName.trim())?0.5:1 }}
              >
                {editingTask ? '💾 保存修改' : '➕ 创建任务'}
              </button>
            </div>
          </div>
        </div>
      )}

      <footer className="app-footer">
        <p>© 2026 Prolockey 洛科安全防护用品有限公司 · 视频拍摄任务管理系统 · 内部使用</p>
      </footer>
    </div>
  );
};

export default TaskManager;
